import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, useWindowDimensions, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useActionSheet } from '@expo/react-native-action-sheet';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import {
	createEmptyProfilePromptDraft,
	getPeopleProfilePromptAnswers,
	getProfilePromptOptions,
	PEOPLE_PROFILE_ABOUT_YOU_DISCLAIMER,
	PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH,
	PEOPLE_PROFILE_PROMPT_MAX_ANSWERS,
	resolveProfileAboutYouStatus,
	toProfilePromptDrafts,
	toProfilePromptInput,
} from '../../../common/utils/people/profilePrompts';
import {
	GET_MY_PROFILE_ABOUT_YOU_MOBILE,
	MODIFY_MY_PROFILE_ABOUT_YOU_MOBILE,
} from '../../graphql/people';
import { Text, TextInput, UserAvatar } from '../../components';
import { ActionDock, Card, EmptyState, Screen, SecondaryButton, SectionTitle } from '../../design/components';
import { Alert } from '../../components/other/Alert';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineQueue } from '../../offline';
import { useTheme } from '../../theme';

const getGraphQLErrorMessage = (error, fallback) => error?.graphQLErrors?.[0]?.message || fallback;

// Self-service profile photo and get to know you answers, mirroring the Sentry People
// My account Profile panel. The photo saves as soon as it uploads; answers save with the button.
const MyProfileAboutYouView = ({ captureMedia, capturedMediaLink, processingMedia }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors, type } } } = useTheme();
	const { isOnline } = useOfflineQueue();
	const { showActionSheetWithOptions } = useActionSheet();
	const { height: windowHeight } = useWindowDimensions();

	const { data, loading, error, refetch } = useQuery(GET_MY_PROFILE_ABOUT_YOU_MOBILE, {
		fetchPolicy: 'cache-and-network',
	});
	const [modifyProfileAboutYou] = useMutation(MODIFY_MY_PROFILE_ABOUT_YOU_MOBILE);

	const employee = data?.getMyEmployeeProfile || null;
	const employeeId = employee?.id;
	const storedAnswers = employee?.profilePrompts;
	const storedAnswersKey = useMemo(() => JSON.stringify(getPeopleProfilePromptAnswers(storedAnswers)), [storedAnswers]);

	const [drafts, setDrafts] = useState(() => toProfilePromptDrafts(storedAnswers));
	const [isDirty, setIsDirty] = useState(false);
	const [showValidation, setShowValidation] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isPhotoSaving, setIsPhotoSaving] = useState(false);
	const processedPhotoUrl = useRef(null);

	useEffect(() => {
		setDrafts(toProfilePromptDrafts(JSON.parse(storedAnswersKey)));
		setIsDirty(false);
		setShowValidation(false);
	}, [employeeId, storedAnswersKey]);

	useEffect(() => {
		if (!capturedMediaLink || !employeeId || processedPhotoUrl.current === capturedMediaLink) {
			return;
		}

		processedPhotoUrl.current = capturedMediaLink;
		const savePhoto = async () => {
			setIsPhotoSaving(true);
			try {
				await modifyProfileAboutYou({
					variables: { input: { id: employeeId, profilePhotoUrl: capturedMediaLink } },
				});
				Toast.show('Profile photo updated');
			}
			catch (error) {
				Toast.show(getGraphQLErrorMessage(error, 'Unable to update the profile photo'));
			}
			finally {
				setIsPhotoSaving(false);
			}
		};
		savePhoto();
	}, [capturedMediaLink, employeeId, modifyProfileAboutYou]);

	const status = resolveProfileAboutYouStatus({
		photoUrl: employee?.profilePhotoUrl,
		drafts,
		storedAnswers,
	});

	const changeDrafts = useCallback((nextDrafts) => {
		setDrafts(nextDrafts);
		setIsDirty(true);
	}, []);

	const updateRow = useCallback((index, updates) => {
		changeDrafts(drafts.map((row, rowIndex) => (rowIndex === index ? { ...row, ...updates } : row)));
	}, [changeDrafts, drafts]);

	const removeRow = useCallback((index) => {
		const nextRows = drafts.filter((_, rowIndex) => rowIndex !== index);
		changeDrafts(nextRows.length ? nextRows : [createEmptyProfilePromptDraft()]);
	}, [changeDrafts, drafts]);

	const addRow = useCallback(() => {
		changeDrafts([...drafts, createEmptyProfilePromptDraft()]);
	}, [changeDrafts, drafts]);

	// An action sheet instead of a dropdown, so the long questions are shown in full.
	const chooseQuestion = useCallback((index) => {
		const options = getProfilePromptOptions({ drafts, index, storedAnswers });
		showActionSheetWithOptions(
			{
				title: 'Choose a question',
				options: [...options.map(option => option.label), 'Cancel'],
				cancelButtonIndex: options.length,
				// Cap the sheet so the long question list scrolls instead of pushing the title and first questions off screen.
				containerStyle: { backgroundColor: colors.surface, maxHeight: windowHeight * 0.85 },
				textStyle: { ...type.body, color: colors.text },
				titleTextStyle: { ...type.secondary, color: colors.textSecondary },
				tintColor: colors.text,
			},
			(buttonIndex) => {
				if (buttonIndex === undefined || buttonIndex >= options.length) {
					return;
				}

				updateRow(index, { key: options[buttonIndex].value });
			},
		);
	}, [colors, drafts, showActionSheetWithOptions, storedAnswers, type, updateRow, windowHeight]);

	const choosePhoto = useCallback(() => {
		// Offline the picker keeps a local file, which cannot become a profile photo.
		if (!isOnline) {
			Alert.alert('Internet connection required', 'Your photo needs to upload before it can be saved.');
			return;
		}

		captureMedia();
	}, [captureMedia, isOnline]);

	const saveAnswers = useCallback(async () => {
		setShowValidation(true);
		if (status.promptsError) {
			return;
		}

		if (!isOnline) {
			Alert.alert('Internet connection required', 'Connect to the internet to save your answers.');
			return;
		}

		setIsSaving(true);
		try {
			await modifyProfileAboutYou({
				variables: { input: { id: employeeId, profilePrompts: toProfilePromptInput(status.answers) } },
			});
			setIsDirty(false);
			setShowValidation(false);
			Toast.show('Answers saved');
		}
		catch (error) {
			Toast.show(getGraphQLErrorMessage(error, 'Unable to save the answers'));
		}
		finally {
			setIsSaving(false);
		}
	}, [employeeId, isOnline, modifyProfileAboutYou, status.answers, status.promptsError]);

	const screenProps = {
		backLabel: 'My account',
		heading: 'Profile photo and about you',
	};

	// A failed load is not the same as having no employee profile, so offer a retry instead.
	if (!employee) {
		return (
			<Screen
				{...screenProps}
				isLoading={loading}
				error={error ? 'Your profile could not be loaded. Check your internet connection and try again.' : undefined}
				onRetry={() => refetch().catch(() => null)}
			>
				<EmptyState
					icon="user-x"
					title="No employee profile"
					message="No employee profile is linked to this account. Your photo and answers can be added once you have an employee profile."
				/>
			</Screen>
		);
	}

	const name = [employee.preferredName || employee.firstName, employee.lastName].filter(Boolean).join(' ');
	const isPhotoBusy = processingMedia || isPhotoSaving;

	return (
		<Screen
			{...screenProps}
			subheading={PEOPLE_PROFILE_ABOUT_YOU_DISCLAIMER}
			dock={(
				<ActionDock
					action={{
						label: 'Save answers',
						onPress: saveAnswers,
						loading: isSaving,
						disabled: !isDirty || isSaving,
					}}
				>
					{showValidation && status.promptsError ? (
						<Text style={styles.errorText}>{status.promptsError}</Text>
					) : null}
				</ActionDock>
			)}
		>
			<Card style={styles.card}>
				<SectionTitle title="Profile photo" />
				<Text style={styles.mutedText}>Use a clear, recent photo of yourself. Square photos work best.</Text>
				<View style={styles.photoRow}>
					<UserAvatar name={name} photoUrl={employee.profilePhotoUrl} size={80} />
					<SecondaryButton
						label={isPhotoBusy ? 'Uploading photo...' : (employee.profilePhotoUrl ? 'Change photo' : 'Upload photo')}
						icon="camera"
						onPress={choosePhoto}
						disabled={isPhotoBusy}
						style={styles.photoButton}
					/>
				</View>
			</Card>

			<Card style={styles.card}>
				<SectionTitle title="Get to know you" />
				<Text style={styles.mutedText}>
					{`Pick 1 to ${PEOPLE_PROFILE_PROMPT_MAX_ANSWERS} questions and answer each in up to ${PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH} characters.`}
				</Text>
				{drafts.map((row, index) => (
					<View key={index} style={styles.promptRow}>
						<View style={styles.promptQuestionRow}>
							<Pressable
								onPress={() => chooseQuestion(index)}
								disabled={isSaving}
								accessibilityRole="button"
								accessibilityLabel="Choose a question"
								style={({ pressed }) => [styles.questionButton, pressed && styles.pressed]}
							>
								<Text style={row.key ? styles.questionText : styles.questionPlaceholder}>
									{getProfilePromptOptions({ drafts, index, storedAnswers })
										.find(option => option.value === row.key)?.label || 'Choose a question'}
								</Text>
								<Feather name="chevron-down" size={18} color={colors.iconMuted} />
							</Pressable>
							{drafts.length > 1 ? (
								<Pressable
									onPress={() => removeRow(index)}
									disabled={isSaving}
									accessibilityRole="button"
									accessibilityLabel="Remove question"
									style={({ pressed }) => [styles.removeButton, pressed && styles.pressed]}
								>
									<Feather name="x-circle" size={22} color={colors.iconMuted} />
								</Pressable>
							) : null}
						</View>
						<TextInput
							value={row.answer}
							onChange={answer => updateRow(index, { answer })}
							placeholder="Your answer"
							multiline={true}
							maxLength={PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH}
							disabled={isSaving}
							style={styles.answerInput}
						/>
						<Text style={styles.counter}>
							{`${String(row.answer || '').length}/${PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH}`}
						</Text>
					</View>
				))}
				{drafts.length < PEOPLE_PROFILE_PROMPT_MAX_ANSWERS ? (
					<SecondaryButton
						label="Add another question"
						icon="plus"
						onPress={addRow}
						disabled={isSaving}
						style={styles.addButton}
					/>
				) : null}
			</Card>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	card: {
		gap: space.xs,
	},
	mutedText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	photoRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.lg,
		marginTop: space.md,
	},
	photoButton: {
		flexShrink: 1,
	},
	promptRow: {
		marginTop: space.md,
	},
	promptQuestionRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: space.sm,
	},
	questionButton: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		minHeight: touch.min,
		paddingVertical: 10,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.canvas,
	},
	questionText: {
		...type.rowTitleSemibold,
		flex: 1,
		color: colors.text,
	},
	questionPlaceholder: {
		...type.rowText,
		flex: 1,
		color: colors.textSecondary,
	},
	removeButton: {
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
	answerInput: {
		minHeight: 72,
		marginBottom: space.xs,
	},
	counter: {
		...type.caption,
		color: colors.textSecondary,
		textAlign: 'right',
	},
	addButton: {
		alignSelf: 'flex-start',
		marginTop: space.md,
	},
	errorText: {
		...type.secondary,
		color: colors.danger,
	},
});

const MyProfileAboutYouViewWithMediaPicker = withMediaPicker(MyProfileAboutYouView);

// Same folder as the profile photos uploaded in Sentry People and Sentry settings.
const MyProfileAboutYouViewWithPicker = props => (
	<MyProfileAboutYouViewWithMediaPicker s3Path="image/users/" {...props} />
);

export default MyProfileAboutYouViewWithPicker;
